---
title: "Заполненные поля PDF пропали после сохранения: что проверить"
description: "Почему заполненная PDF-форма выглядит пустой у получателя и когда стоит скачать копию с зафиксированными полями AcroForm."
locale: "ru"
date: "2026-08-03"
tags: ["pdf", "формы", "acroform", "фиксация полей"]
relatedTools: ["fill-pdf-form", "flatten-pdf"]
translations:
  en: "pdf-form-fields-disappear-after-saving"
faq:
  - q: "Чем обычное сохранение формы отличается от фиксации полей?"
    a: "Редактируемая копия сохраняет интерактивные поля. В копии с зафиксированными полями видимые значения AcroForm становятся содержимым страниц, а интерактивные поля удаляются."
  - q: "Можно ли потом исправить значение в копии с зафиксированными полями?"
    a: "Через поле формы уже нельзя. Сохраните исходную или редактируемую копию отдельно, если ответы ещё могут измениться."
  - q: "Какие формы поддерживает pdfy?"
    a: "Стандартные AcroForm с текстовыми полями, флажками, радиокнопками, выпадающими списками и списками с одиночным или множественным выбором. XFA, сканы и произвольное добавление полей не поддерживаются."
  - q: "Форма отправляется на сервер?"
    a: "Нет. Проверка, заполнение и фиксация полей по выбору пользователя выполняются локально в браузере."
---

import { AcroFormFiller } from '@/features/acroform-filler'

Анкета на 12 страниц заполнена, сохранена и отправлена. У получателя вместо
фамилии, адреса и галочек снова пустой бланк. Самая неприятная проверка здесь
простая: откройте именно скачанный файл, а не вкладку, где вы его заполняли.
Затем откройте ту же копию во втором просмотрщике.

Если ответы есть только в одном просмотрщике, проблема обычно не в тексте, а в
интерактивном слое формы. Значения полей и их видимое представление могут
храниться отдельно. Один PDF-просмотрщик дорисует значение, другой покажет пустое
поле, а печать заберёт только фон страницы.

Для стандартной AcroForm можно сделать копию с зафиксированными полями: сохранить
видимые значения на страницах и удалить редактируемые поля. В форме ниже включите
«Зафиксировать поля после заполнения» перед скачиванием. Исходный файл оставьте
отдельно, если ответы ещё придётся менять.

<div className="my-8 rounded-xl border bg-card p-5 shadow-sm">
  <AcroFormFiller />
</div>

## Сначала понять, какой PDF перед вами

Кликаете в поле и появляется курсор? Скорее всего, это интерактивная форма.
[Заполнение PDF-формы](/zapolnit-pdf-formu) поддерживает стандартные AcroForm:
текст, флажки, радиокнопки, выпадающие и множественные списки. Лимит одной
копии - 32 МБ, 200 страниц и 500 полей.

Если это только скан и клики не показывают интерактивных полей, заполнять
инструменту нечего. Он не угадывает места для фамилии и подписи и не запускает
OCR. Надпись `Please
wait...` вместо формы часто указывает на XFA, старый отдельный формат форм.
XFA тоже не превращается в AcroForm одним переключателем.

Есть ещё третий случай: поля существуют, но автор запретил заполнение или файл
зашифрован. Обходить ограничения не надо. Нужна незапароленная копия от автора
или версия формы, предназначенная для заполнения.

## Что именно меняет фиксация полей

У pdfy фиксация относится к полям AcroForm. Страница не превращается целиком в
картинку, но текстовые поля, галочки и выбранные варианты перестают быть
редактируемыми контролами. Это полезно перед отправкой анкеты в систему, печатью
или открытием в неизвестном PDF-просмотрщике.

Это не пароль, не КЭП и не защита документа от любых правок. Комментарии,
выделения, штампы, рукописные пометки и XFA-only элементы не входят в этот
режим. Если форма уже заполнена в другом приложении, можно отдельно
[зафиксировать поля PDF](/flatten-pdf). Если заполняете с нуля, переключатель во встроенной
форме делает обе операции за один проход.

Вся обработка идёт в браузере. Перед отправкой всё равно откройте готовую
`-flattened.pdf` копию, проверьте пару длинных полей, все галочки и последнюю
страницу. А исходник не удаляйте до подтверждения от получателя. Больше всего
времени съедает не повторное заполнение, а попытка восстановить уже
зафиксированную опечатку без оригинала.

Другие инструменты разложены в
[карте PDF-инструментов](/blog/besplatnye-pdf-instrumenty-bez-registracii).
